import React, { useState, useEffect, useCallback } from 'react';
import { getFeedbackList, getFeedbackStats, updateFeedbackStatus, deleteFeedback } from '../api';
import { ConfirmDialog } from './shared/UIComponents';

const FeedbackManager = () => {
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, open, in_progress, resolved, closed
  const [feedbackType, setFeedbackType] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState({ status: '', adminNotes: '' });
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  

  const loadFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getFeedbackList(filter === 'all' ? null : filter, feedbackType === 'all' ? null : feedbackType);
      setFeedback(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter, feedbackType]);

  const loadStats = useCallback(async () => {
    try {
      const data = await getFeedbackStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  useEffect(() => {
    loadFeedback();
    loadStats();
  }, [loadFeedback, loadStats]);

  const handleStatusUpdate = async () => {
    if (!selectedFeedback || !statusUpdate.status) return;

    try {
      await updateFeedbackStatus(
        selectedFeedback.id, 
        statusUpdate.status, 
        statusUpdate.adminNotes || null
      );
      
      // Reload feedback and stats
      loadFeedback();
      loadStats();
      
      // Close modal and reset
      setShowStatusModal(false);
      setSelectedFeedback(null);
      setStatusUpdate({ status: '', adminNotes: '' });
      
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (feedbackId) => {
    try {
      await deleteFeedback(feedbackId);
      loadFeedback();
      loadStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setPendingDeleteId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'text-blue-600 bg-blue-100';
      case 'in_progress':
        return 'text-yellow-600 bg-yellow-100';
      case 'resolved':
        return 'text-green-600 bg-green-100';
      case 'closed':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'open':
        return '🔵 Open';
      case 'in_progress':
        return '🟡 In Progress';
      case 'resolved':
        return '🟢 Resolved';
      case 'closed':
        return '⚫ Closed';
      default:
        return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low':
        return 'text-green-600 bg-green-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'high':
        return 'text-orange-600 bg-orange-100';
      case 'critical':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'low':
        return '🟢 Low';
      case 'medium':
        return '🟡 Medium';
      case 'high':
        return '🟠 High';
      case 'critical':
        return '🔴 Critical';
      default:
        return priority;
    }
  };

  const getFeedbackTypeLabel = (type) => {
    switch (type) {
      case 'general_feedback':
        return 'General';
      case 'bug_report':
        return 'Bug Report';
      case 'feature_request':
        return 'Feature Request';
      case 'model_improvement':
        return 'Model Improvement';
      default:
        return type;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  if (loading && !feedback.length) {
    return <div>Loading feedback...</div>;
  }

  if (error) {
    return <div style={{ color: 'red' }}>Error: {error}</div>;
  }

  return (
    <div className="feedback-manager">
      <h3>User Feedback Management</h3>
      
      {/* Statistics */}
      {stats && (
        <div className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="text-2xl font-bold text-blue-600">{stats.total_feedback}</div>
            <div className="text-sm text-blue-800">Total Feedback</div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="text-2xl font-bold text-blue-600">{stats.by_status?.open || 0}</div>
            <div className="text-sm text-blue-800">Open</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="text-2xl font-bold text-yellow-600">{stats.by_status?.in_progress || 0}</div>
            <div className="text-sm text-yellow-800">In Progress</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-2xl font-bold text-green-600">{stats.by_status?.resolved || 0}</div>
            <div className="text-sm text-green-800">Resolved</div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-gray-600">{stats.by_status?.closed || 0}</div>
            <div className="text-sm text-gray-800">Closed</div>
          </div>
        </div>
      )}

      {/* Filter Controls */}
      <div className="mb-4 flex flex-wrap gap-2">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        
        <select
          value={feedbackType}
          onChange={(e) => setFeedbackType(e.target.value)}
          className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Types</option>
          <option value="general_feedback">General Feedback</option>
          <option value="bug_report">Bug Report</option>
          <option value="feature_request">Feature Request</option>
          <option value="model_improvement">Model Improvement</option>
        </select>
      </div>

      {/* Feedback List */}
      {feedback.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No feedback found with the current filters.
        </div>
      ) : (
        <div className="space-y-4">
          {feedback.map((item) => (
            <div key={item.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-medium text-gray-900">{item.subject}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                      {getStatusLabel(item.status)}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(item.priority)}`}>
                      {getPriorityLabel(item.priority)}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-600 mb-2">
                    <span className="font-medium">Type:</span> {getFeedbackTypeLabel(item.feedback_type)}
                    {item.category && (
                      <>
                        <span className="mx-2">•</span>
                        <span className="font-medium">Category:</span> {item.category}
                      </>
                    )}
                  </div>
                  
                  <p className="text-gray-700 mb-2">{item.description}</p>
                  
                  <div className="text-xs text-gray-500 space-y-1">
                    {item.user_name && (
                      <div><span className="font-medium">From:</span> {item.user_name}</div>
                    )}
                    {item.user_email && (
                      <div><span className="font-medium">Email:</span> {item.user_email}</div>
                    )}
                    <div><span className="font-medium">Created:</span> {formatTimestamp(item.created_at)}</div>
                    {item.admin_notes && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                        <span className="font-medium">Admin Notes:</span> {item.admin_notes}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => {
                      setSelectedFeedback(item);
                      setStatusUpdate({ status: item.status, adminNotes: item.admin_notes || '' });
                      setShowStatusModal(true);
                    }}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                  >
                    Update Status
                  </button>
                  <button
                    onClick={() => setPendingDeleteId(item.id)}
                    className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Update Feedback Status</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={statusUpdate.status}
                    onChange={(e) => setStatusUpdate(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Admin Notes (Optional)
                  </label>
                  <textarea
                    value={statusUpdate.adminNotes}
                    onChange={(e) => setStatusUpdate(prev => ({ ...prev, adminNotes: e.target.value }))}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Add admin notes..."
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setSelectedFeedback(null);
                    setStatusUpdate({ status: '', adminNotes: '' });
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={Boolean(pendingDeleteId)}
        title="Delete feedback?"
        message="This will permanently remove the feedback entry."
        confirmLabel="Delete"
        onConfirm={() => handleDelete(pendingDeleteId)}
        onCancel={() => setPendingDeleteId(null)}
      />
    </div>
  );
};

export default FeedbackManager;

